"use client";

import { useState, useEffect, useCallback } from 'react';
import Pagination from '../components/Pagination';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Download,
  Upload,
  Search,
  Filter,
  RefreshCw,
  Database,
  FileText,
  Video,
  Image,
  Music,
  Settings,
  Users,
  Lock,
  Unlock,
  X,
  Check,
  AlertCircle,
  Archive,
  ArrowUpDown,
  Undo2
} from 'lucide-react';
import { APIURL } from '../components/Api.jsx';

export default function MasterDataPage() {
  const [masterData, setMasterData] = useState([]);
  const [targetAudienceOptions, setTargetAudienceOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMasterData, setSelectedMasterData] = useState(null);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showTogglePublicConfirmModal, setShowTogglePublicConfirmModal] = useState(false);
  const [showToggleStatusConfirmModal, setShowToggleStatusConfirmModal] = useState(false);
  const [showRevokeConfirmModal, setShowRevokeConfirmModal] = useState(false);
  const [pendingAccessUsers, setPendingAccessUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [pendingAction, setPendingAction] = useState(null); // To store the action details
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [refreshing, setRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  // Form data
  const initialFormData = {
    name: '',
    description: '',
    dataType: '',
    category: '',
    subCategory: '',
    tags: '',
    difficultyLevel: '',
    targetAudience: '',
    isActive: true,
    isPublic: false,
    sortOrder: 0,
    files: []
  };
  const [formData, setFormData] = useState(initialFormData);

  const dataTypeOptions = [
    { label: 'Document', value: 'PDF_DOCUMENT' },
    { label: 'Video Content', value: 'VIDEO_CONTENT' },
    { label: 'Image Asset', value: 'IMAGE_ASSET' }
  ];

  const difficultyLevelOptions = [
    { label: 'Beginner', value: 'BEGINNER' },
    { label: 'Intermediate', value: 'INTERMEDIATE' },
    { label: 'Advanced', value: 'ADVANCED' },
    { label: 'Expert', value: 'EXPERT' }
  ];

  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB


  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: 'success' }), 3000);
  };

  const getAcceptValue = (dataType) => {
    switch (dataType) {
      case 'PDF_DOCUMENT':
        return '.pdf';
      case 'VIDEO_CONTENT':
        return 'video/*';
      case 'IMAGE_ASSET':
        return 'image/*';
      default:
        return '*/*';
    }
  };

  const isFileTypeValid = (file, dataType) => {
    if (!dataType) return true;
    const type = file.type;
    const name = file.name.toLowerCase();

    switch (dataType) {
      case 'PDF_DOCUMENT':
        const documentExtensions = ['.pdf'];
        return documentExtensions.some(ext => name.endsWith(ext));
      case 'VIDEO_CONTENT':
        return type.startsWith('video/');
      case 'IMAGE_ASSET':
        return type.startsWith('image/');
      default:
        return true;
    }
  };

  const handleFileUpload = (event) => {
    if (!formData.dataType) {
      showToast('Please select a Data Type first', 'error');
      setErrors({ ...errors, dataType: 'Please select a Data Type first' });
      event.target.value = '';
      return;
    }

    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];
    const invalidTypeFiles = [];
    const oversizedFiles = [];

    selectedFiles.forEach(file => {
      const isTypeValid = isFileTypeValid(file, formData.dataType);
      const isSizeValid = file.size <= MAX_FILE_SIZE;

      if (isTypeValid && isSizeValid) {
        validFiles.push(file);
      } else if (!isTypeValid) {
        invalidTypeFiles.push(file.name);
      } else if (!isSizeValid) {
        oversizedFiles.push(file.name);
      }
    });

    if (invalidTypeFiles.length > 0 || oversizedFiles.length > 0) {
      event.target.value = '';
      if (invalidTypeFiles.length > 0) {
        setErrors(prev => ({ ...prev, files: `Invalid file type: ${invalidTypeFiles.join(', ')}` }));
      } else {
        setErrors(prev => ({ ...prev, files: `File too large: ${oversizedFiles.join(', ')} (Max 100MB)` }));
      }
      return;
    }

    setFormData({ ...formData, files: [...formData.files, ...validFiles] });
    // Clear file error if files are added
    if (errors.files) {
      setErrors({ ...errors, files: null });
    }
  };

  const removeFile = (index) => {
    const updatedFiles = formData.files.filter((_, i) => i !== index);
    setFormData({ ...formData, files: updatedFiles });
  };

  const fetchMasterData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      if (isRefresh) setErrors({});

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${APIURL}/api/v1/admin/master-data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMasterData(data);
      } else {
        showToast('Failed to fetch master data', 'error');
      }
    } catch (error) {
      showToast('Error fetching master data', 'error');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    return {
      'Authorization': token ? `${tokenType} ${token}` : '',
      'Content-Type': 'application/json'
    };
  };

  const handleRefresh = () => {
    fetchMasterData(true);
  };

  useEffect(() => {
    fetchMasterData();
    fetchTargetAudiences();
  }, [fetchMasterData]);

  const fetchTargetAudiences = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules/target-audiences`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        // Map the API response to options with displayName as the label
        const options = data.map(item => ({
          label: item.displayName, // Use displayName as the label
          value: item.value // Use value as the value
        }));
        setTargetAudienceOptions(options);
      } else {
        console.error('Failed to fetch target audiences:', response.status);
        showToast('Failed to fetch target audiences', 'error');
      }
    } catch (error) {
      console.error('Error fetching target audiences:', error);
      showToast('Error fetching target audiences', 'error');
    }
  };



  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'This field is required';
    if (!formData.dataType) newErrors.dataType = 'This field is required';
    if (!formData.files || formData.files.length === 0) {
      newErrors.files = 'This field is required';
    } else {
      const oversized = formData.files.filter(f => f.size > MAX_FILE_SIZE);
      if (oversized.length > 0) {
        newErrors.files = `Files too large: ${oversized.map(f => f.name).join(', ')} (Max 100MB)`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      const token = localStorage.getItem('accessToken');
      const multipart = new FormData();
      // Append JSON fields
      multipart.append('name', formData.name ?? '');
      multipart.append('description', formData.description ?? '');
      multipart.append('dataType', formData.dataType ?? '');
      multipart.append('category', formData.category ?? '');
      multipart.append('subCategory', formData.subCategory ?? '');
      multipart.append('tags', formData.tags ?? '');
      multipart.append('difficultyLevel', formData.difficultyLevel ?? '');
      multipart.append('targetAudience', formData.targetAudience ?? '');
      multipart.append('isActive', String(formData.isActive));
      multipart.append('isPublic', String(formData.isPublic));
      multipart.append('sortOrder', String(formData.sortOrder ?? 0));
      // Append files as 'file' (Swagger: file not files)
      (formData.files || []).forEach((f) => multipart.append('file', f));

      const response = await fetch(`${APIURL}/api/v1/admin/master-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: multipart
      });

      if (response.ok) {
        showToast('Master data created successfully');
        setShowCreateDialog(false);
        resetForm();
        fetchMasterData();
      } else {
        showToast('Failed to create master data', 'error');
      }
    } catch (error) {
      showToast('Error creating master data', 'error');
    }
  };

  // Function to detect changes between original and current form data
  const getChangedFields = () => {
    if (!originalFormData) return {};

    const changes = {};
    const fieldsToCheck = [
      'name', 'description', 'dataType', 'category', 'subCategory',
      'tags', 'difficultyLevel', 'targetAudience', 'isActive', 'isPublic', 'sortOrder'
    ];

    fieldsToCheck.forEach(field => {
      if (formData[field] !== originalFormData[field]) {
        changes[field] = formData[field];
      }
    });

    // Check for file changes
    if (formData.files && formData.files.length > 0) {
      changes.files = formData.files;
    }

    return changes;
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;

    try {
      const token = localStorage.getItem('accessToken');
      const changedFields = getChangedFields();

      // If no changes detected, show message and return
      if (Object.keys(changedFields).length === 0) {
        showToast('No changes detected', 'info');
        return;
      }

      const multipart = new FormData();

      // Only append changed fields
      Object.keys(changedFields).forEach(key => {
        if (key === 'files') {
          changedFields[key].forEach((f) => multipart.append('file', f));
        } else {
          multipart.append(key, String(changedFields[key]));
        }
      });

      console.log('Sending only changed fields:', changedFields);

      const response = await fetch(`${APIURL}/api/v1/admin/master-data/${selectedMasterData.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: multipart
      });

      if (response.ok) {
        showToast('Master data updated successfully');
        setShowEditDialog(false);
        resetForm();
        setOriginalFormData(null);
        fetchMasterData();
      } else {
        showToast('Failed to update master data', 'error');
      }
    } catch (error) {
      showToast('Error updating master data', 'error');
    }
  };

  // const handleDelete = async (id) => {
  //   if (window.confirm('Are you sure you want to delete this master data?')) {
  //     try {
  //       const token = localStorage.getItem('accessToken');
  //       const response = await fetch(`${APIURL}/api/v1/admin/master-data/${id}`, {
  //         method: 'DELETE',
  //         headers: {
  //           'Authorization': `Bearer ${token}`,
  //           'Content-Type': 'application/json'
  //         }
  //       });

  //       if (response.ok) {
  //         showToast('Master data deleted successfully');
  //         fetchMasterData();
  //       } else {
  //         showToast('Failed to delete master data', 'error');
  //       }
  //     } catch (error) {
  //       showToast('Error deleting master data', 'error');
  //     }
  //   }
  // };

  const handleDelete = async (id) => {
    // Instead of using window.confirm, we'll set up the modal
    setPendingAction({ type: 'delete', id });
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${APIURL}/api/v1/admin/master-data/${id}/access`, {
        headers
      });

      if (response.ok) {
        const users = await response.json();
        setPendingAccessUsers(users);
        setPendingAction({ type: 'delete', id });
        setShowDeleteConfirmModal(true);
      } else {
        showToast("Failed to check user access before deletion.", "error");
      }
    } catch (error) {
      showToast("Error checking user access.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeAccessClick = async (id) => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${APIURL}/api/v1/admin/master-data/${id}/access`, {
        headers
      });

      if (response.ok) {
        const users = await response.json();
        setPendingAccessUsers(users);
        setPendingAction({ type: 'revokeAccess', id });
        setShowRevokeConfirmModal(true);
      } else {
        showToast("Failed to check user access.", "error");
      }
    } catch (error) {
      showToast("Error checking user access.", "error");
    } finally {
      setLoading(false);
    }
  };

  const executeRevokeAll = async (id, isFromDelete = false) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${APIURL}/api/v1/admin/master-data/${id}/access/revoke-all`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        showToast("Master Data access revoked from all users successfully.");
        setPendingAccessUsers([]);
        fetchMasterData();
      } else {
        showToast("Failed to revoke Master Data access.", "error");
      }
    } catch (error) {
      showToast("Error revoking Master Data access.", "error");
    }
  };

  const executeDelete = async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${APIURL}/api/v1/admin/master-data/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showToast('Master data deleted successfully');
        fetchMasterData();
      } else {
        showToast('Failed to delete master data', 'error');
      }
    } catch (error) {
      showToast('Error deleting master data', 'error');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '-';

    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));

    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };



  const handleToggleStatus = async (id, currentStatus) => {
    // Instead of directly toggling, we'll set up the modal
    setPendingAction({ type: 'toggleStatus', id, currentStatus });
    setShowToggleStatusConfirmModal(true);
  };

  const executeToggleStatus = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('accessToken');
      const newStatus = !currentStatus;
      const response = await fetch(`${APIURL}/api/v1/admin/master-data/${id}/toggle-status?isActive=${newStatus}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showToast(`Status ${newStatus ? 'activated' : 'deactivated'} successfully`);
        fetchMasterData();
      } else {
        showToast('Failed to update status', 'error');
      }
    } catch (error) {
      showToast('Error updating status', 'error');
    }
  };

  // const handleTogglePublic = async (id, currentPublicStatus) => {
  //   try {
  //     const token = localStorage.getItem('accessToken');
  //     const newPublicStatus = !currentPublicStatus;
  //     const response = await fetch(`${APIURL}/api/v1/admin/master-data/${id}/toggle-public?isPublic=${newPublicStatus}`, {
  //       method: 'PUT',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json'
  //       }
  //     });

  //     if (response.ok) {
  //       showToast(`Visibility ${newPublicStatus ? 'made public' : 'made private'} successfully`);
  //       fetchMasterData();
  //     } else {
  //       showToast('Failed to update visibility status', 'error');
  //     }
  //   } catch (error) {
  //     showToast('Error updating visibility status', 'error');
  //   }
  // };

  const handleTogglePublic = async (id, currentPublicStatus) => {
    // Instead of directly toggling, we'll set up the modal
    setPendingAction({ type: 'togglePublic', id, currentPublicStatus });
    setShowTogglePublicConfirmModal(true);
  };

  const executeTogglePublic = async (id, currentPublicStatus) => {
    try {
      const token = localStorage.getItem('accessToken');
      const newPublicStatus = !currentPublicStatus;
      const response = await fetch(`${APIURL}/api/v1/admin/master-data/${id}/toggle-public?isPublic=${newPublicStatus}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showToast(`Visibility ${newPublicStatus ? 'made public' : 'made private'} successfully`);
        fetchMasterData();
      } else {
        showToast('Failed to update visibility status', 'error');
      }
    } catch (error) {
      showToast('Error updating visibility status', 'error');
    }
  };


  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      dataType: '',
      category: '',
      subCategory: '',
      tags: '',
      difficultyLevel: '',
      targetAudience: '',
      isActive: true,
      isPublic: false,
      sortOrder: 0,
      files: []
    });
    setOriginalFormData(null);
    setErrors({});
  };

  const openEditDialog = (data) => {
    setSelectedMasterData(data);
    const initialFormData = {
      name: data.name || '',
      description: data.description || '',
      dataType: data.dataType || data.data_type || '',
      category: data.category || '',
      subCategory: data.subCategory || data.sub_category || '',
      tags: data.tags || '',
      difficultyLevel: data.difficultyLevel || data.difficulty_level || '',
      targetAudience: data.targetAudience || data.target_audience || '',
      isActive: data.isActive !== undefined ? data.isActive : (data.is_active !== undefined ? data.is_active : true),
      isPublic: data.isPublic !== undefined ? data.isPublic : (data.is_public !== undefined ? data.is_public : false),
      sortOrder: data.sortOrder || data.sort_order || 0,
      files: data.files || []
    };
    setFormData(initialFormData);
    setOriginalFormData(initialFormData);
    setErrors({});
    setShowEditDialog(true);
  };

  const openCreateDialog = () => {
    setFormData(initialFormData);
    setErrors({});
    setShowCreateDialog(true);
  };

  const openViewDialog = (data) => {
    setSelectedMasterData(data);
    setShowViewDialog(true);
  };

  const openAccessDialog = (data) => {
    setSelectedMasterData(data);
    setShowAccessDialog(true);
  };

  const getDataTypeIcon = (dataType) => {
    switch (dataType) {
      case 'PDF_DOCUMENT':
        return <FileText className="w-4 h-4" />;
      case 'VIDEO_CONTENT':
        return <Video className="w-4 h-4" />;
      case 'IMAGE_ASSET':
        return <Image className="w-4 h-4" aria-label="Image asset" />;
      case 'AUDIO_CONTENT':
        return <Music className="w-4 h-4" />;
      default:
        return <Database className="w-4 h-4" />;
    }
  };

  const filteredData = (() => {
    let filtered = masterData.filter(item => {
      const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = !filterType || item.dataType === filterType;
      const matchesStatus = !filterStatus ||
        (filterStatus === 'active' && item.isActive) ||
        (filterStatus === 'inactive' && !item.isActive);

      return matchesSearch && matchesType && matchesStatus;
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'name') {
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
        } else if (sortConfig.key === 'type') {
          aValue = (a.dataType || '').toLowerCase();
          bValue = (b.dataType || '').toLowerCase();
        } else if (sortConfig.key === 'category') {
          aValue = (a.category || '').toLowerCase();
          bValue = (b.category || '').toLowerCase();
        } else if (sortConfig.key === 'status') {
          aValue = (a.isActive || a.is_active) ? 'active' : 'inactive';
          bValue = (b.isActive || b.is_active) ? 'active' : 'inactive';
        } else if (sortConfig.key === 'visibility') {
          aValue = (a.isPublic || a.is_public) ? 'public' : 'private';
          bValue = (b.isPublic || b.is_public) ? 'public' : 'private';
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  })();

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '↑' : '↓';
    }
    return '';
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMasterData = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, filterStatus]);

  return (
    <div className="p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-indigo-100 p-3 rounded-lg">
                  <Archive className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Manage(add/edit/visibility access management/delete) all the pdfs, videos, diet chats and assign them to the required specific users. This is the repository of the application containing all the content.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh master data"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <button
              onClick={openCreateDialog}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Master Data
            </button>

          </div>

          <div className="flex gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
            >
              <option value="">All Types</option>
              {dataTypeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sl. No.</th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Name
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('name')}</span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center">
                      Type
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('type')}</span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center">
                      Category
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('category')}</span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('status')}</span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('visibility')}
                  >
                    <div className="flex items-center">
                      Visibility
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('visibility')}</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentMasterData.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{indexOfFirstItem + index + 1}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        {getDataTypeIcon(item.dataType)}
                        <span>{item.dataType?.replace(/_/g, ' ') || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.category ? (
                        item.category
                      ) : (
                        <span className="text-xl font-semibold">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(item.isActive || item.is_active) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {(item.isActive || item.is_active) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(item.isPublic || item.is_public) ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {(item.isPublic || item.is_public) ? 'Public' : 'Private'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openViewDialog(item)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditDialog(item)}
                          className="text-yellow-600 hover:text-yellow-900 p-1 rounded-md hover:bg-yellow-50 transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {/* Access Dialog Button */}
                        {/* <button
                                onClick={() => openAccessDialog(item)}
                                className="text-purple-600 hover:text-purple-900 p-1 rounded-md hover:bg-purple-50 transition-colors"
                                title="Manage Access"
                            >
                              <Users className="w-4 h-4" />
                            </button> */}
                        <button
                          onClick={() => handleToggleStatus(item.id, item.isActive || item.is_active)}
                          className={`p-1 rounded-md transition-colors ${(item.isActive || item.is_active)
                            ? "text-red-600 hover:text-red-900 hover:bg-red-50"
                            : "text-green-600 hover:text-green-900 hover:bg-green-50"
                            }`}
                          title={(item.isActive || item.is_active) ? "Deactivate Status" : "Activate Status"}
                        >
                          {(item.isActive || item.is_active) ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleTogglePublic(item.id, item.isPublic || item.is_public)}
                          className={`p-1 rounded-md transition-colors ${(item.isPublic || item.is_public)
                            ? "text-orange-600 hover:text-orange-900 hover:bg-orange-50"
                            : "text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50"
                            }`}
                          title={(item.isPublic || item.is_public) ? "Make Private (Hide from Users)" : "Make Public (Show to Users)"}
                        >
                          {(item.isPublic || item.is_public) ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleRevokeAccessClick(item.id)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50 transition-colors"
                          title="Revoke Access from All Users"
                        >
                          <Undo2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredData.length === 0 && !loading && (
          <div className="p-8 text-center text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No master data found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalElements={filteredData.length}
        pageSize={itemsPerPage}
        setPageSize={setItemsPerPage}
        itemName="Master Data"
      />

      {/* Create Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl text-black font-bold">Create Master Data</h2>
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-black text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (errors.name) setErrors({ ...errors, name: null });
                  }}
                  placeholder="Enter name"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-black text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                />
              </div>

              {/* Compact row with 3 fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-black mb-1">Data Type *</label>
                  <select
                    value={formData.dataType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      if (formData.files.length > 0 && newType !== formData.dataType) {
                        const confirmClear = window.confirm('Changing Data Type will clear previously selected files as they may not be compatible. Continue?');
                        if (!confirmClear) return;
                        setFormData({ ...formData, dataType: newType, files: [] });
                      } else {
                        setFormData({ ...formData, dataType: newType });
                      }
                      if (errors.dataType) setErrors({ ...errors, dataType: null });
                    }}
                    className={`w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.dataType ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="" disabled hidden>Select type</option>
                    {dataTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.dataType && <p className="text-red-500 text-xs mt-1">{errors.dataType}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Category"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>

              </div>

              {/* Compact row with 3 fields */}
              <div className="grid grid-cols-2 gap-3">

                <div>
                  <label className="block text-xs font-medium text-black mb-1">Sub Category</label>
                  <input
                    type="text"
                    value={formData.subCategory}
                    onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                    placeholder="Sub category"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-black mb-1">Difficulty</label>
                  <select
                    value={formData.difficultyLevel}
                    onChange={(e) => setFormData({ ...formData, difficultyLevel: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  >
                    <option value="" disabled hidden>Select level</option>
                    {difficultyLevelOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

              </div>

              <div>
                <label className="block text-black text-sm font-medium mb-2">Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="Enter tags (comma separated)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                />
              </div>

              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-black font-medium">Active</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-black font-medium">Public</span>
                </label>
              </div>

              {/* File Upload Section */}
              <div>
                <label className="block text-black text-sm font-medium mb-2">Files *</label>
                <div className={`border-2 border-dashed rounded-lg p-3 ${errors.files ? 'border-red-500' : 'border-gray-300'}`}>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    accept={getAcceptValue(formData.dataType)}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center justify-center py-2"
                  >
                    <svg className={`w-6 h-6 mb-1 ${errors.files ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className={`text-xs ${errors.files ? 'text-red-500' : 'text-gray-600'}`}>Click to upload or drag files</span>
                  </label>
                </div>
                {errors.files && <p className="text-red-500 text-xs mt-1">{errors.files}</p>}

                {/* Display selected files (names only, no type) */}
                {formData.files.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-xs font-medium text-black mb-1">Selected Files:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {formData.files.map((file, index) => (
                        <li key={index} className="text-xs text-black flex items-center justify-between">
                          <span className="truncate max-w-[200px]">{file.name}</span>
                          <button onClick={() => removeFile(index)} className="text-red-500 hover:text-red-700 text-xs ml-2">Remove</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={Object.keys(errors).length > 0}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${Object.keys(errors).length > 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {showEditDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl text-black font-bold">Edit Master Data</h2>
              <button
                onClick={() => {
                  setShowEditDialog(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (errors.name) setErrors({ ...errors, name: null });
                  }}
                  placeholder="Enter name"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                />
              </div>

              {/* Compact row with 3 fields */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-black mb-1">Data Type *</label>
                  <select
                    value={formData.dataType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      if (formData.files.length > 0 && newType !== formData.dataType) {
                        const confirmClear = window.confirm('Changing Data Type will clear previously selected files as they may not be compatible. Continue?');
                        if (!confirmClear) return;
                        setFormData({ ...formData, dataType: newType, files: [] });
                      } else {
                        setFormData({ ...formData, dataType: newType });
                      }
                      if (errors.dataType) setErrors({ ...errors, dataType: null });
                    }}
                    className={`w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.dataType ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="" disabled hidden>Select type</option>
                    {dataTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.dataType && <p className="text-red-500 text-xs mt-1">{errors.dataType}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Category"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1">Sub Category</label>
                  <input
                    type="text"
                    value={formData.subCategory}
                    onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                    placeholder="Sub category"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>
              </div>

              {/* Compact row with 3 fields */}
              <div className="grid grid-cols-2 gap-3">


                <div>
                  <label className="block text-xs font-medium text-black mb-1">Difficulty</label>
                  <select
                    value={formData.difficultyLevel}
                    onChange={(e) => setFormData({ ...formData, difficultyLevel: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  >
                    <option value="">Select level</option>
                    {difficultyLevelOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="Enter tags (comma separated)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                />
              </div>

              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-black font-medium">Active</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-black font-medium">Public</span>
                </label>
              </div>

              {/* File Upload Section */}
              <div>
                <label className="block text-black text-sm font-medium mb-2">Files *</label>
                <div className={`border-2 border-dashed rounded-lg p-3 ${errors.files ? 'border-red-500' : 'border-gray-300'}`}>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    accept={getAcceptValue(formData.dataType)}
                    className="hidden"
                    id="file-upload-edit"
                  />
                  <label
                    htmlFor="file-upload-edit"
                    className="cursor-pointer flex flex-col items-center justify-center py-2"
                  >
                    <svg className={`w-6 h-6 mb-1 ${errors.files ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className={`text-xs ${errors.files ? 'text-red-500' : 'text-black'}`}>Click to upload or drag files</span>
                  </label>
                </div>
                {errors.files && <p className="text-red-500 text-xs mt-1">{errors.files}</p>}

                {/* Display selected files (names only) */}
                {formData.files.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-xs font-medium text-black mb-1">Selected Files:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {formData.files.map((file, index) => (
                        <li key={index} className="text-xs text-black flex items-center justify-between">
                          <span className="truncate max-w-[200px]">{file.name}</span>
                          <button onClick={() => removeFile(index)} className="text-red-500 hover:text-red-700 text-xs ml-2">Remove</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowEditDialog(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={Object.keys(errors).length > 0}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${Object.keys(errors).length > 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Dialog */}
      {showViewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl text-black font-bold">View Master Data</h2>
              <button onClick={() => setShowViewDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {selectedMasterData && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black">ID</label>
                    <p className="text-sm text-black">{selectedMasterData.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black">Name</label>
                    <p className="text-sm text-black font-semibold">{selectedMasterData.name}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black">Description</label>
                  <p className="text-sm text-black">{selectedMasterData.description ? (
                    selectedMasterData.description
                  ) : (
                    <span className="text-xl font-semibold">-</span>
                  )}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black">Data Type</label>
                    <p className="text-sm text-black">
                      {(selectedMasterData.data_type || selectedMasterData.dataType)?.replace(/_/g, ' ') || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black">Category</label>
                    <p className="text-sm text-black">{selectedMasterData.category ? (
                      selectedMasterData.category
                    ) : (
                      <span className="text-xl font-semibold">-</span>
                    )}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black">Sub Category</label>
                    <p className="text-sm text-black">{selectedMasterData.sub_category || selectedMasterData.subCategory || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black">Tags</label>
                    <p className="text-sm text-black">{selectedMasterData.tags || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black">Difficulty Level</label>
                    <p className="text-sm text-black">{selectedMasterData.difficulty_level || selectedMasterData.difficultyLevel || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(selectedMasterData.is_active || selectedMasterData.isActive) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                      {(selectedMasterData.is_active || selectedMasterData.isActive) ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black">Visibility</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(selectedMasterData.is_public || selectedMasterData.isPublic) ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {(selectedMasterData.is_public || selectedMasterData.isPublic) ? 'Public' : 'Private'}
                    </span>
                  </div>
                </div>


                {(selectedMasterData.file_url || selectedMasterData.fileUrl) && (
                  <div>
                    <label className="block text-sm font-medium text-black">File Information</label>
                    <div className="space-y-2 text-sm">
                      <div className=" text-black">
                        <span className="font-medium text-black">File Name:</span> {selectedMasterData.fileName || '-'}
                      </div>
                      <div className=" text-black">
                        <span className="font-medium text-black">File Size:</span> {formatBytes(selectedMasterData.fileSizeBytes) || '-'}
                      </div>
                      <div>
                        <a
                          href={selectedMasterData.file_url || selectedMasterData.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline break-all"
                        >
                          Download File
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black">Created At</label>
                    <p className="text-sm text-black">
                      {new Date(selectedMasterData.created_at || selectedMasterData.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black">Updated At</label>
                    <p className="text-sm text-black">
                      {new Date(selectedMasterData.updated_at || selectedMasterData.updatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Access Management Dialog */}
      {showAccessDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Manage Access</h2>
              <button onClick={() => setShowAccessDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center py-8">
              <Settings className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">Access Management</h3>
              <p className="text-gray-500">This feature will be implemented in the next phase</p>
              <p className="text-sm text-gray-400">Manage user access to master data entries</p>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-red-600">
                {pendingAccessUsers?.length > 0 ? 'Access Revocation Required' : 'Confirm Deletion'}
              </h2>
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setPendingAction(null);
                  setPendingAccessUsers([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              {pendingAccessUsers?.length > 0 ? (
                <>
                  <p className="text-gray-700">
                    This Master Data is currently accessed by <span className="font-bold text-red-600">{pendingAccessUsers.length}</span> user(s).
                  </p>
                  <p className="text-gray-700">
                    You must revoke access for all users before you can delete this item.
                  </p>
                </>
              ) : (
                <p className="text-gray-700">
                  Are you sure you want to delete this master data? This action cannot be undone.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setPendingAction(null);
                  setPendingAccessUsers([]);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              {pendingAccessUsers?.length > 0 ? (
                <button
                  onClick={() => {
                    if (pendingAction && pendingAction.id) {
                      executeRevokeAll(pendingAction.id);
                    }
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Revoke Access
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (pendingAction && pendingAction.type === 'delete') {
                      executeDelete(pendingAction.id);
                    }
                    setShowDeleteConfirmModal(false);
                    setPendingAction(null);
                    setPendingAccessUsers([]);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}


      {showTogglePublicConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-blue-600">Confirm Visibility Change</h2>
              <button
                onClick={() => {
                  setShowTogglePublicConfirmModal(false);
                  setPendingAction(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700 mb-6">
              {pendingAction && pendingAction.currentPublicStatus
                ? 'Are you sure you want to make this master data private?'
                : 'Are you sure you want to make this master data public?'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowTogglePublicConfirmModal(false);
                  setPendingAction(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (pendingAction && pendingAction.type === 'togglePublic') {
                    executeTogglePublic(pendingAction.id, pendingAction.currentPublicStatus);
                  }
                  setShowTogglePublicConfirmModal(false);
                  setPendingAction(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showToggleStatusConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-orange-600">Confirm Status Change</h2>
              <button
                onClick={() => {
                  setShowToggleStatusConfirmModal(false);
                  setPendingAction(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700 mb-6">
              {pendingAction && pendingAction.currentStatus
                ? 'Are you sure you want to deactivate this master data?'
                : 'Are you sure you want to activate this master data?'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowToggleStatusConfirmModal(false);
                  setPendingAction(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (pendingAction && pendingAction.type === 'toggleStatus') {
                    executeToggleStatus(pendingAction.id, pendingAction.currentStatus);
                  }
                  setShowToggleStatusConfirmModal(false);
                  setPendingAction(null);
                }}
                className={`px-4 py-2 text-white rounded-lg ${pendingAction && pendingAction.currentStatus
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
                  }`}
              >
                {pendingAction && pendingAction.currentStatus ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRevokeConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-indigo-600">Revoke Access</h2>
              <button
                onClick={() => {
                  setShowRevokeConfirmModal(false);
                  setPendingAction(null);
                  setPendingAccessUsers([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700 mb-6">
              {pendingAccessUsers?.length > 0
                ? `This Master Data is currently accessed by ${pendingAccessUsers.length} user(s). Do you want to revoke access from all users?`
                : "This Master Data is not accessed by any user."}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRevokeConfirmModal(false);
                  setPendingAction(null);
                  setPendingAccessUsers([]);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {pendingAccessUsers?.length > 0 ? "Cancel" : "Close"}
              </button>
              {pendingAccessUsers?.length > 0 && (
                <button
                  onClick={() => {
                    if (pendingAction && pendingAction.type === 'revokeAccess') {
                      executeRevokeAll(pendingAction.id);
                    }
                    setShowRevokeConfirmModal(false);
                    setPendingAction(null);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Revoke Access
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}